/**
 * ProposalCard — one curator proposal awaiting the nurse's decision.
 *
 * Two affordances, accept and reject, and nothing else.
 *
 * The card used to carry a third, "adjust the label", which let the nurse edit
 * the curator's wording and send the edited label along with the accept. That
 * is gone with the promote flow it belonged to (adr/0025): `:accept` takes no
 * body. Accepting declares the skill and cites the proposal as evidence; the
 * proposal keeps the curator's words and the declaration carries the nurse's.
 * Two records, two writers, and neither rewrites the other. A label she wants
 * in her own words is a declaration she makes, not an edit to what the curator
 * said.
 *
 * `reasoning` is why the curator raised it, read from the timeline behind the
 * skill — the only route that carries it. It is shown quietly under the label
 * because it is the audit trail she judges the proposal by, never the headline.
 * When the timeline read has not landed or failed, the card says so rather than
 * rendering an empty paragraph: accepting a proposal whose reasoning silently
 * failed to load is accepting the curator's words unread.
 *
 * There is no strength or confidence number. The visible unit is "familiar
 * with / worked with", never a percentage or a credential.
 *
 * DESIGN.md: pill radius on CTAs, scale-0.95 press, single accent, 44px
 * targets, flat hairline card on surface-bg, no shadow.
 */

import { Check, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'
import type { HeldSkill, SkillCoding } from '@frontend/api/endpoints/skills'
import { useSkillTimeline } from '@frontend/hooks/useSkills'
import { heldSkillTitle } from '@/lib/skillTitle'

// Mirrors SkillBadges' coding badge — same tint, same pill, no second accent.
function CodingBadge({ coding }: { coding: SkillCoding }) {
  return (
    <span
      className="inline-flex items-center rounded-full bg-[var(--color-citation-50)] px-2.5 py-0.5 text-[12px] font-semibold text-[var(--color-citation-700)]"
      title={`${coding.system}:${coding.code}`}
    >
      {coding.display || `${coding.system}:${coding.code}`}
    </span>
  )
}

/**
 * The curator's reasoning for this skill, read from the records behind it.
 *
 * Its three states are all visible. A failed or pending read never renders as
 * "the curator gave no reason", which would invite a decision on evidence the
 * nurse was never shown.
 */
function Reasoning({ skillKey }: { skillKey: string }) {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useSkillTimeline(skillKey, true)

  if (isLoading) {
    return (
      <p className="text-[13px] leading-relaxed text-[var(--color-surface-mute)]">
        {t('skills.proposal.reasoningLoading')}
      </p>
    )
  }
  if (isError) {
    return (
      <p className="text-[13px] leading-relaxed text-[var(--color-tone-warn)]">
        {t('skills.proposal.reasoningFailed')}
      </p>
    )
  }

  const proposal = data?.items.find((record) => record.kind === 'proposal')
  if (!proposal) {
    return (
      <p className="text-[13px] leading-relaxed text-[var(--color-tone-warn)]">
        {t('skills.proposal.reasoningMissing')}
      </p>
    )
  }
  return (
    <p className="text-[13px] leading-relaxed text-[var(--color-surface-mute)]">
      {proposal.reasoning}
    </p>
  )
}

interface ProposalCardProps {
  skill: HeldSkill
  /** Both verbs carry the fold's version as `If-Match`, so the caller takes it from the skill. */
  onAccept: (skill: HeldSkill) => void
  onReject: (skill: HeldSkill) => void
  busy: boolean
}

export function ProposalCard({ skill, onAccept, onReject, busy }: ProposalCardProps) {
  const { t } = useTranslation()
  const title = heldSkillTitle(skill)

  return (
    <li className="flex flex-col gap-3 rounded-[18px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-5 py-4">
      <p className="text-[17px] font-semibold text-[var(--color-surface-ink)]">{title}</p>

      <Reasoning skillKey={skill.key} />

      {skill.coding ? (
        <div>
          <CodingBadge coding={skill.coding} />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
        <Button
          type="button"
          size="pill"
          onClick={() => onAccept(skill)}
          disabled={busy}
          className="min-h-11 shrink-0"
          aria-label={t('skills.proposal.acceptAriaLabel', { label: title })}
        >
          {busy ? <Spinner className="h-4 w-4" /> : <Check className="h-4 w-4" />}
          {t('skills.proposal.accept')}
        </Button>

        <Button
          type="button"
          variant="ghost"
          onClick={() => onReject(skill)}
          disabled={busy}
          className="min-h-11 rounded-full px-4 text-[var(--color-surface-mute)]"
          aria-label={t('skills.proposal.rejectAriaLabel', { label: title })}
        >
          {busy ? <Spinner className="h-4 w-4" /> : <X className="h-4 w-4" />}
          {t('skills.proposal.reject')}
        </Button>
      </div>
    </li>
  )
}

export default ProposalCard
